import { z } from 'zod';
import { severitySchema } from './shared/severity';
/**
 * Match types for custom capability rules.
 * Aligns with the backend rule engine's ManifestRule.matchType.
 */
export const customCapabilityRuleMatchTypeSchema = z.enum([
    'exists',
    'exact',
    'glob',
    'arrayContains',
    'lengthGt',
]);
/**
 * Severity levels for custom capability rules.
 * All five levels are available — routing (info/low → capability, medium+ → finding)
 * is handled by the backend scanner.
 */
export const customCapabilityRuleSeveritySchema = z.enum([
    'info',
    'low',
    'medium',
    'high',
    'critical',
]);
/**
 * Schema for a single custom capability rule defined by the customer.
 * Rules are evaluated against extension manifests through the same rule engine
 * used for the built-in catalog.
 */
export const customCapabilityRuleSchema = z
    .object({
    field: z.string().min(1),
    matchType: customCapabilityRuleMatchTypeSchema,
    value: z.string().optional(),
    reason: z.string().min(1),
    severity: customCapabilityRuleSeveritySchema,
})
    .refine(data => {
    // 'exists' does not need a value; all other match types require one
    if (data.matchType === 'exists')
        return true;
    return data.value !== undefined && data.value.length > 0;
}, {
    message: 'value is required when matchType is exact, glob, arrayContains, or lengthGt',
    path: ['value'],
});
// Extension Security settings
export const extensionSecuritySettingsSchema = z
    .object({
    dangerousPermissions: z
        .array(z.object({
        permission: z.string(),
        reason: z.string(),
        severity: severitySchema, // Allows 'critical' for known malware patterns
    }))
        .optional(),
    untrustedPublishers: z
        .array(z.object({
        pattern: z.string(),
        reason: z.string(),
        severity: severitySchema, // Allows 'critical' for known malware patterns
    }))
        .optional(),
    excessiveCommandsThreshold: z
        .string()
        .regex(/^[0-9]+$/)
        .optional(),
    extensionsCodeScan: z.boolean().optional(),
    extensionsPackagesOsvDetection: z.boolean().optional(),
    // Custom capability rules — evaluated via the manifest rule engine
    customCapabilityRules: z.array(customCapabilityRuleSchema).optional(),
    // Auto-removal settings (Agent only)
    autoRemoveVulnerableExtensions: z.boolean().optional(),
    // Legacy key - kept for backward compatibility with existing stored settings
    autoRemoveBlockedExtensions: z.boolean().optional(),
    removalSeverityThreshold: severitySchema.optional(),
})
    .strict();
//# sourceMappingURL=extension-security.js.map